import * as React from 'react';
import {FluxContext, SyncFluxContext} from '../Flux';
import {TFlux} from '../types';

/**
 * This shouldn't be exported from the package.
 * @deprecated createUseFluxContext to construct a type safe hook instead!
 */
export function useUnsafeInternalFluxContext<T extends TFlux<any, any, any>>() {
  const flux_ = React.useContext(FluxContext);

  if (flux_) {
    return flux_ as T;
  }

  throw new Error(
    'Could not find any Flux context. Ensure your app wrapped with either <FluxContext.Provider> or <IsolatedFluxProvider />.'
  );
}

export function useSyncFluxContext() {
  return React.useContext(SyncFluxContext);
}

/**
 * Creates a typed flux context hook that provides type safety for the flux instance. If you are
 * using this hook be sure that you really need it. Most use cases can be handled by dispatch & selectors.
 *
 * @type State - The state shape of your stores
 * @type LegacyCreators - The type of legacy action creators
 * @type SpecificActions - The action types that can be dispatched
 * @returns A React hook that returns a typed flux instance, you will then use this hook in your components.
 *
 * @example
 * ```tsx
 * // Define your state and action types
 * type CounterState = {
 *   count: number;
 * };
 *
 * type IncrementAction = { type: 'INCREMENT' };
 * type DecrementAction = { type: 'DECREMENT' };
 * type CounterActions = IncrementAction | DecrementAction;
 *
 * // Create a typed flux context hook
 * const useCounterFlux = createUseFluxContext<CounterState, CounterActions>();
 *
 * // Use in a component
 * function CounterComponent() {
 *   const flux = useCounterFlux();
 *   // Now I have flux! But you probably could have used a selector!
 *   return <></>;
 * }
 * ```
 */
export function createUseFluxContext<
  State extends Record<any, any> = Record<any, any>,
  SpecificActions extends {type: string} = {type: string},
  LegacyCreators extends Record<any, any> = Record<any, any>,
>() {
  function useTypedFluxContext() {
    const flux_ = useUnsafeInternalFluxContext() as TFlux<
      State,
      SpecificActions,
      LegacyCreators
    >;
    return flux_;
  }

  return useTypedFluxContext;
}
